"use client";

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button as DsButton, Textarea } from '@/components/ds';
import { Flag, Check } from 'lucide-react';
import {
  REPORT_DETAILS_MAX,
  REPORT_REASONS,
  ReportReason,
  ReportTargetType,
} from '@/lib/reports';
import { useToast } from '@/hooks/useToast';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { cn } from '@/lib/utils';

interface ReportContentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetType: ReportTargetType;
  targetId: string;
  targetUserId?: string | null;
  targetLabel?: string;
  onSubmitted?: () => void;
}

export default function ReportContentModal({
  open,
  onOpenChange,
  targetType,
  targetId,
  targetUserId,
  targetLabel,
  onSubmitted,
}: ReportContentModalProps) {
  const { user } = useAuthContext();
  const { toast } = useToast();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason(null);
      setDetails('');
      setSubmitting(false);
    }
  }, [open]);

  const close = () => {
    if (submitting) return;
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!user) {
      toast({
        title: 'Sign in to report content',
        description: 'You need an account so our team can follow up.',
        variant: 'destructive',
      });
      return;
    }
    if (!reason) return;

    setSubmitting(true);
    try {
      const res = await fetch('/api/reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_type: targetType,
          target_id: targetId,
          target_user_id: targetUserId ?? null,
          reason,
          details: details.trim() || null,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 409 || data?.code === 'ALREADY_REPORTED') {
        toast({
          title: "You've already reported this",
          description: 'Our team is reviewing it.',
        });
        onOpenChange(false);
        onSubmitted?.();
        return;
      }
      if (!res.ok) {
        throw new Error(data?.error || `Request failed (${res.status})`);
      }

      toast({
        title: 'Report submitted',
        description: 'Our team will review it shortly.',
        variant: 'success',
      });
      onOpenChange(false);
      onSubmitted?.();
    } catch (err) {
      console.error('[Report] submit failed:', err);
      toast({
        title: 'Could not submit report',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const remaining = REPORT_DETAILS_MAX - details.length;
  const targetNoun =
    targetType === 'post'
      ? 'post'
      : targetType === 'comment'
      ? 'comment'
      : targetType === 'profile'
      ? 'profile'
      : targetType === 'group'
      ? 'group'
      : targetType === 'message'
      ? 'message'
      : 'job';

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className={cn(
          // Replace the shadcn `bg-background` default with the design-system
          // surface, and override every default that doesn't match the rest
          // of the app's modals (border, radius, padding, layout).
          'bg-surface-1 text-text-primary border border-white/[0.08] shadow-2xl shadow-black/40',
          // The shadcn DialogContent default is `grid gap-4 p-6` — switch
          // to a flex column so the scrollable body region works.
          '!grid-cols-1 !grid-rows-[auto_1fr_auto] p-0 gap-0',
          // Mobile full-screen, desktop centered card. !max-w-full on mobile
          // beats the default max-w-lg.
          'rounded-none sm:rounded-2xl !max-w-full sm:!max-w-[480px]',
          'h-[100dvh] sm:h-auto max-h-[100dvh] sm:max-h-[85vh] w-full',
          // Reduced-motion: skip slide/zoom, keep just the fade.
          'motion-reduce:!animate-none motion-reduce:data-[state=open]:!fade-in-0 motion-reduce:data-[state=closed]:!fade-out-0'
        )}
      >
        {/* Header — visible Flag icon, headline, sub-copy. The X close
            button is rendered by DialogContent automatically (top-right). */}
        <DialogHeader className="px-5 pt-5 pb-4 pr-12 border-b border-white/[0.06] text-left">
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-text-primary">
            <Flag className="h-5 w-5 text-status-busy" strokeWidth={1.75} />
            Report this {targetNoun}
          </DialogTitle>
          <DialogDescription className="text-sm text-text-secondary mt-1">
            {targetLabel
              ? `What's wrong with "${targetLabel}"? Reports are confidential.`
              : 'Tell us what’s wrong. Reports are confidential.'}
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable body so the 10 reasons + textarea never push the
            footer off-screen. */}
        <div className="overflow-y-auto px-5 py-5 space-y-5">
          <fieldset className="space-y-2" disabled={submitting}>
            <legend className="text-[13px] text-text-secondary mb-2">
              Why are you reporting this?
            </legend>
            <div className="grid grid-cols-1 gap-1.5">
              {REPORT_REASONS.map((r) => {
                const selected = reason === r.value;
                return (
                  <button
                    key={r.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setReason(r.value)}
                    disabled={submitting}
                    className={cn(
                      'flex items-center gap-3 px-3.5 py-2.5 rounded-md border text-left transition-all',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-surface-1',
                      selected
                        ? 'border-accent bg-accent-soft text-text-primary'
                        : 'border-white/[0.08] bg-surface-2 text-text-secondary hover:bg-surface-3 hover:border-white/[0.16] hover:text-text-primary'
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border transition-colors',
                        selected
                          ? 'border-accent bg-accent text-text-on-accent'
                          : 'border-border-default bg-surface-1'
                      )}
                      aria-hidden="true"
                    >
                      {selected && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
                    </span>
                    <span className="text-sm">{r.label}</span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="space-y-1.5">
            <label
              htmlFor="report-details"
              className="block text-[13px] text-text-secondary"
            >
              Tell us more <span className="text-text-tertiary">(optional)</span>
            </label>
            <Textarea
              id="report-details"
              value={details}
              onChange={(e) =>
                setDetails(e.target.value.slice(0, REPORT_DETAILS_MAX))
              }
              placeholder="Add context that will help us review."
              disabled={submitting}
              rows={4}
              maxLength={REPORT_DETAILS_MAX}
              className="min-h-[96px]"
            />
            <p
              className={cn(
                'text-[11px] text-right tabular-nums',
                remaining < 50 ? 'text-status-busy' : 'text-text-tertiary'
              )}
            >
              {remaining} characters left
            </p>
          </div>
        </div>

        {/* Footer — sticky at the bottom of the card so the primary action
            is always reachable on mobile. Cancel uses ds ghost; Submit uses
            ds danger to convey moderation intent. */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-white/[0.06] bg-surface-1">
          <DsButton
            variant="ghost"
            size="md"
            onClick={close}
            disabled={submitting}
          >
            Cancel
          </DsButton>
          <DsButton
            variant="danger"
            size="md"
            onClick={handleSubmit}
            disabled={!reason || submitting}
          >
            {submitting ? 'Submitting…' : 'Submit report'}
          </DsButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
